import {
  alpha2FlagEmoji,
  alpha3ToAlpha2,
  type BoardBasis,
  formatFullName,
  type TaskStatus,
  taskStatusValues,
  UNIT_TYPES,
} from "@vidimo/shared";

import type {
  ReservationSource,
  ReservationStatus,
  UnitReadiness,
} from "@vidimo/shared/enums";
import type { useTranslations } from "use-intl";
import type { Guest } from "@/lib/collections/guests";
import type { Task } from "@/lib/collections/tasks";

export type LabelsTranslator = ReturnType<typeof useTranslations<"labels">>;

const WHITESPACE_RE = /\s+/u;
const LETTER_RE = /\p{L}/u;

export { cn } from "@vidimo/ui";

// Status Labels

export function getTaskStatusLabel(
  t: LabelsTranslator,
  status: Task["status"]
): string {
  const isKnownStatus = (taskStatusValues as readonly string[]).includes(
    status
  );
  return isKnownStatus ? t(`taskStatus.${status as TaskStatus}`) : status;
}

/** Undefined for a unit type id this build does not know. */
export function getUnitTypeLabel(
  t: LabelsTranslator,
  unitTypeId: string
): string | undefined {
  const unitType = UNIT_TYPES.find(({ id }) => id === unitTypeId);
  return unitType && t(`unitType.${unitType.id}`);
}

export function getBoardBasisLabel(
  t: LabelsTranslator,
  board: BoardBasis
): string {
  return t(`boardBasis.${board}`);
}

/** The collection's `serverEnum` already closes the union, so no narrowing. */
export function getReservationStatusLabel(
  t: LabelsTranslator,
  status: ReservationStatus
): string {
  return t(`reservationStatus.${status}`);
}

/** OTA names are brands and read the same in every language. */
const RESERVATION_SOURCE_NAMES: Record<
  Exclude<ReservationSource, "direct" | "other">,
  string
> = {
  airbnb: "Airbnb",
  booking_com: "Booking.com",
  vrbo: "Vrbo",
};

/** Human label for a reservation's origin. `sourceDetail` (agency name,
 *  "Walk-in", a smaller OTA) wins over the coarse "Other" bucket when set. */
export function getReservationSourceLabel(
  t: LabelsTranslator,
  source: ReservationSource,
  sourceDetail?: string | null
): string {
  if (source === "other" && sourceDetail?.trim()) {
    return sourceDetail.trim();
  }
  if (source === "direct" || source === "other") {
    return t(`reservationSource.${source}`);
  }
  return RESERVATION_SOURCE_NAMES[source];
}

// Status Color Utilities

// Centralized color mapping for reservation statuses
export const reservationStatusColors: Record<
  ReservationStatus,
  { hex: string; tailwind: string }
> = {
  cancelled: {
    hex: "#ef4444",
    tailwind: "bg-red-500/20 text-red-800 dark:text-red-400 border-red-500/30",
  },
  checked_in: {
    hex: "#22c55e",
    tailwind:
      "bg-green-500/20 text-green-800 dark:text-green-400 border-green-500/30",
  },
  checked_out: {
    hex: "#6b7280",
    tailwind:
      "bg-gray-500/20 text-gray-600 dark:text-gray-400 border-gray-500/30",
  },
  confirmed: {
    hex: "#a855f7",
    tailwind:
      "bg-purple-500/20 text-purple-700 dark:text-purple-400 border-purple-500/30",
  },
  // Muted rather than alarming: a decline is a deliberate, closed outcome, not
  // the disruption a cancellation or a no-show is. Deliberately darker than
  // `checked_out`'s grey: both are inactive states, so the hues are close on
  // purpose, but the two must still be told apart where only the colour shows
  // (the avatar ring).
  declined: {
    hex: "#475569",
    tailwind:
      "bg-slate-600/20 text-slate-700 dark:text-slate-300 border-slate-600/30",
  },
  no_show: {
    hex: "#f97316",
    tailwind:
      "bg-orange-500/20 text-orange-800 dark:text-orange-400 border-orange-500/30",
  },
  requested: {
    hex: "#3b82f6",
    tailwind:
      "bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30",
  },
};

// Centralized color mapping for task statuses
export const taskStatusColors: Record<
  Task["status"],
  { hex: string; tailwind: string }
> = {
  cancelled: {
    hex: "#ef4444",
    tailwind: "bg-red-500/20 text-red-800 dark:text-red-400 border-red-500/30",
  },
  completed: {
    hex: "#22C55E",
    tailwind:
      "bg-green-500/20 text-green-800 dark:text-green-400 border-green-500/30",
  },
  in_progress: {
    hex: "#06b6d4",
    tailwind:
      "bg-cyan-500/20 text-cyan-700 dark:text-cyan-400 border-cyan-500/30",
  },
  // Zinc — closed and owed by nobody, so it reads quieter than cancelled's red.
  missed: {
    hex: "#71717a",
    tailwind:
      "bg-zinc-500/20 text-zinc-700 dark:text-zinc-400 border-zinc-500/30",
  },
  // Slate — reads as "on hold / idle", distinct from pending's amber and
  // in_progress's cyan so a paused card is glanceable on the board.
  paused: {
    hex: "#64748b",
    tailwind:
      "bg-slate-500/20 text-slate-600 dark:text-slate-400 border-slate-500/30",
  },
  pending: {
    hex: "#f59e0b",
    tailwind:
      "bg-amber-500/20 text-amber-800 dark:text-amber-400 border-amber-500/30",
  },
};

/** The collection's `serverEnum` already closes the union, so no narrowing. */
export function getUnitReadinessLabel(
  t: LabelsTranslator,
  readiness: UnitReadiness
): string {
  return t(`unitReadiness.${readiness}`);
}

/**
 * Centralized color mapping for unit readiness.
 *
 * Green for the two states a guest may be sent into, so "can I use this room"
 * reads at a glance without parsing the word; amber for dirty (work outstanding,
 * matching a pending task); red for out of order, which is the only one that is
 * not a step in the daily cycle.
 */
export const unitReadinessColors: Record<
  UnitReadiness,
  { hex: string; tailwind: string }
> = {
  clean: {
    hex: "#22C55E",
    tailwind:
      "bg-green-500/20 text-green-800 dark:text-green-400 border-green-500/30",
  },
  dirty: {
    hex: "#f59e0b",
    tailwind:
      "bg-amber-500/20 text-amber-800 dark:text-amber-400 border-amber-500/30",
  },
  // Emerald, one step deeper than clean's green: inspected is the same "ready"
  // answer with a second pass behind it, not a different kind of state.
  inspected: {
    hex: "#059669",
    tailwind:
      "bg-emerald-600/20 text-emerald-800 dark:text-emerald-400 border-emerald-600/30",
  },
  out_of_order: {
    hex: "#ef4444",
    tailwind: "bg-red-500/20 text-red-800 dark:text-red-400 border-red-500/30",
  },
};

// Display Name Helpers

/**
 * Get display name for Staff (or any person with a single name field).
 */
export function getStaffDisplayName(person: { name: string }): string {
  return person.name.trim();
}

/**
 * Get display name for a Guest. Priority: name (if either firstName/lastName) → phone → email.
 * Never returns empty (enforced by Guest schema constraint).
 */
export function getGuestDisplayName(
  guest: Pick<Guest, "email" | "firstName" | "lastName" | "phone">
): string {
  const name = formatFullName(guest);
  if (name) {
    return name;
  }
  if (guest.phone?.trim()) {
    return guest.phone.trim();
  }
  if (guest.email?.trim()) {
    return guest.email.trim();
  }
  return "?";
}

/**
 * Derive initials from a display name (e.g. for avatar fallback).
 * 2+ words → first letter of first two; 1 word → first 2 chars. A name with
 * no letter (a nameless guest's phone number) gives "", so the avatar shows
 * its person icon.
 */
export function getInitialsFromDisplayName(displayName: string): string {
  const trimmed = displayName.trim();
  if (!LETTER_RE.test(trimmed)) {
    return "";
  }
  const parts = trimmed.split(WHITESPACE_RE);
  if (parts.length >= 2) {
    const a = parts[0]?.[0] ?? "";
    const b = parts[1]?.[0] ?? "";
    return (a + b).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

/**
 * How a unit reads on screen: its code and name, minus the duplication.
 *
 * Codes are often already embedded in the name — code "101", name "Room 101" —
 * and joining them blindly produced "101 Room 101". Only prefix the code when
 * the name does not already carry it.
 */
export function formatUnitLabel(
  code: string | null | undefined,
  name: string | null | undefined
): string {
  const trimmedCode = code?.trim() ?? "";
  const trimmedName = name?.trim() ?? "";
  if (!trimmedCode) {
    return trimmedName;
  }
  if (!trimmedName) {
    return trimmedCode;
  }
  const nameCarriesCode = new RegExp(
    `(^|\\s)${trimmedCode.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&")}(\\s|$)`,
    "i"
  ).test(trimmedName);
  return nameCarriesCode ? trimmedName : `${trimmedCode} ${trimmedName}`;
}

/**
 * The flag emoji for a country, from the alpha-3 codes guests carry.
 *
 * Staff refer to guests by origin — "the British couple", "the German family" —
 * so a flag is the one identity field worth showing at a glance. Returns null
 * for anything unrecognised rather than a tofu box.
 */
export function countryFlagEmoji(
  alpha3: string | null | undefined
): string | null {
  return alpha2FlagEmoji(alpha3ToAlpha2(alpha3));
}
